import { escapeHtml } from '../../utils/html.js';

/**
 * SuccessModal - Composant de modale de confirmation de succès unifié
 * 
 * Utilise l'image Validé.png pour une expérience utilisateur cohérente
 * à travers toute l'application.
 * 
 * @param {Object} options - Options de configuration
 * @param {string} options.title - Titre de la modale (défaut: "Succès")
 * @param {string} options.message - Message principal (peut contenir du HTML)
 * @param {string} options.confirmText - Texte du bouton de confirmation (défaut: "PARFAIT")
 * @param {Function} options.onClose - Callback appelé lors de la fermeture (optionnel)
 * @param {number} options.zIndex - z-index de la modale (défaut: 3000)
 * @param {boolean} options.showIcon - Afficher l'icône de succès (défaut: true)
 * @returns {Object} Instance de la modale avec méthodes show(), hide(), destroy(), close()
 */
function createSuccessModal(options = {}) {
  const {
    title = 'Succès',
    message = 'Opération réussie',
    confirmText = 'Parfait',
    onClose = null,
    zIndex = 3000,
    showIcon = true,
  } = options;

  // Créer le conteneur de la modale
  const modal = document.createElement('div');
  modal.className = 'modal modal-validate-confirm';
  modal.style.cssText = `
    display: none;
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.7);
    backdrop-filter: blur(8px);
    align-items: center;
    justify-content: center;
    z-index: ${zIndex};
  `;

  // Construire le HTML de la modale
  modal.innerHTML = `
    <div class="modal-content modal-validate-content" style="max-width: 520px; width: 90%;">
      <div class="modal-validate-body">
        <div class="modal-validate-mouse">
          <img src="/images/souris/VALIDE.png" alt="Succès" class="mouse-animation" />
        </div>
        <div class="modal-validate-content-wrapper">
          ${showIcon ? `
          <div style="display: flex; align-items: center; justify-content: center; margin-bottom: 1rem;">
            <div style="width: 64px; height: 64px; border-radius: 50%; background: rgba(38, 255, 190, 0.15); border: 3px solid rgba(38, 255, 190, 0.4); display: flex; align-items: center; justify-content: center;">
              <span class="material-symbols-outlined" style="font-size: 36px; color: #26FFBE;">check_circle</span>
            </div>
          </div>
          ` : ''}
          <h2 class="modal-validate-title">${escapeHtml(title)}</h2>
          <p class="modal-validate-text">
            ${escapeHtml(message)}
          </p>
        </div>
      </div>
      <div class="modal-validate-footer">
        <button class="modal-validate-btn modal-validate-btn-confirm success-modal-confirm" style="width: 100%;">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
          <span>${escapeHtml(confirmText)}</span>
        </button>
      </div>
    </div>
  `;

  // Récupérer les éléments interactifs
  const confirmBtn = modal.querySelector('.success-modal-confirm');
  const content = modal.querySelector('.modal-content');

  // Fonction pour fermer la modale
  function close() {
    modal.classList.remove('active');
    setTimeout(() => {
      modal.style.display = 'none';
      if (onClose && typeof onClose === 'function') {
        onClose();
      }
    }, 300);
  }

  // Fonction pour afficher la modale
  function show() {
    document.body.appendChild(modal);
    // Force reflow pour l'animation
    modal.offsetHeight;
    modal.style.display = 'flex';
    
    // Animation d'entrée
    setTimeout(() => {
      modal.classList.add('active');
    }, 10);
    
    // Focus sur le bouton de confirmation pour l'accessibilité
    setTimeout(() => confirmBtn?.focus(), 100);
  }

  // Fonction pour détruire la modale
  function destroy() {
    if (modal.parentNode) {
      modal.parentNode.removeChild(modal);
    }
  }

  // Event listeners
  if (confirmBtn) {
    confirmBtn.addEventListener('click', () => {
      close();
      // Détruire la modale après un court délai pour permettre l'animation
      setTimeout(destroy, 300);
    });
  }

  // Fermer sur clic en dehors du contenu
  modal.addEventListener('click', (event) => {
    if (event.target === modal) {
      close();
      setTimeout(destroy, 300);
    }
  });

  // Fermer sur Escape
  const escapeHandler = (event) => {
    if (event.key === 'Escape') {
      close();
      setTimeout(destroy, 300);
      document.removeEventListener('keydown', escapeHandler);
    }
  };
  document.addEventListener('keydown', escapeHandler);

  // Empêcher la propagation des clics sur le contenu
  if (content) {
    content.addEventListener('click', (event) => {
      event.stopPropagation();
    });
  }

  // Retourner l'API publique
  return {
    show,
    hide: close,
    close,
    destroy,
    element: modal,
  };
}

/**
 * Fonction helper pour créer et afficher rapidement une modale de succès
 * 
 * @param {string|Object} messageOrOptions - Message ou objet d'options
 * @param {string} title - Titre (optionnel si premier paramètre est un objet)
 * @param {Function} onClose - Callback de fermeture (optionnel)
 * @returns {Object} Instance de la modale
 */
export function showSuccessModal(messageOrOptions, title = 'Succès', onClose = null) {
  let options = {};
  
  // Support de deux syntaxes : showSuccessModal(options) ou showSuccessModal(message, title, onClose)
  if (typeof messageOrOptions === 'object') {
    options = messageOrOptions;
  } else {
    options = {
      message: messageOrOptions,
      title,
      onClose,
    };
  }

  const modal = createSuccessModal(options);
  modal.show();

  return modal;
}
